import { useQuery } from '@tanstack/react-query'

import type { CountryCodebookDTO } from '@leanpay/models'
import { FinancialDetailsOptionsTypes } from '@leanpay/models'
import { checkoutService } from '@leanpay/services'

import { CheckoutQueryKey } from './cache-keys'

/**
 * Static reference data (countries, employment, marital status).
 */

/**
 * Countries codebook, with the app's own market listed first.
 *
 * @param marketCountryCodebookId codebook id of the market the app runs in
 */
export const useGetCountries = (marketCountryCodebookId: string) => {
    return useQuery({
        queryKey: [CheckoutQueryKey.Country],
        queryFn: () => checkoutService.getCountryCodebook(),
        select: (countries: CountryCodebookDTO[]) => {
            const marketCountry = countries.find((c) => c.id.toString() === marketCountryCodebookId)
            return marketCountry
                ? [
                      marketCountry,
                      ...countries.filter((c) => c.id.toString() !== marketCountryCodebookId),
                  ]
                : countries
        },
    })
}

export const useGetIndustriesOfEmployment = () => {
    return useQuery({
        queryKey: [CheckoutQueryKey.IndustryOfEmployment],
        queryFn: () => checkoutService.getIndustryCodebook(),
    })
}

export const useGetEmploymentTypes = () => {
    return useQuery({
        queryKey: [CheckoutQueryKey.EmploymentType],
        queryFn: () => checkoutService.getEmploymentTypes(),
    })
}

/** Marital-status codebook (the list of selectable options). */
export const useGetMaritalStatuses = () => {
    return useQuery({
        queryKey: [CheckoutQueryKey.MaritalStatuses],
        queryFn: () => checkoutService.getMaritalStatuses(),
    })
}

/** The marital status already stored for the current user — not the codebook. */
export const useLoadMaritalStatus = () => {
    return useQuery({
        queryKey: [CheckoutQueryKey.LoadedMaritalStatus],
        queryFn: () => checkoutService.loadMaritalStatus(),
    })
}

export const useGetNumberOfConsecutivePaymentMonths = () => {
    return useQuery({
        queryKey: [CheckoutQueryKey.NumberOfConsecutivePaymentMonths],
        queryFn: () =>
            checkoutService.getFinancialDetailsOptions(FinancialDetailsOptionsTypes.TYPE_1),
    })
}

export const useGetNumberOfRecentLoans = () => {
    return useQuery({
        queryKey: [CheckoutQueryKey.NumberOfRecentLoans],
        queryFn: () =>
            checkoutService.getFinancialDetailsOptions(FinancialDetailsOptionsTypes.TYPE_2),
    })
}
